import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, Renderer2, TemplateRef } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-badge-info',
  templateUrl: './badge-info.component.html',
  styleUrls: ['./badge-info.component.scss']
})
export class BadgeInfoComponent implements OnDestroy {

  // Indicates whether the tooltip should remain open when the user hovers over it. 
  // If set to true, the tooltip will not close when the user moves the mouse away from the badge, allowing them to interact with the tooltip content.
  @Input() interactiveTooltip: boolean = false;
  @Input() classes: string = 'badge-primary';
  @Input() tooltipClass: string = 'tooltip-bg-secondary';
  @Input() tooltipContent: string | TemplateRef<any> | null = null;
  @Input() closeDelay: number = 0;
  @Input() triggers: string = 'hover';
  @Input() message: any;

  private closeTimeoutId: number | null = null;
  private removeTooltipMouseEnterListener: (() => void) | null = null;
  private removeTooltipMouseLeaveListener: (() => void) | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
  ) {}

  onBadgeEnter(tooltip: NgbTooltip): void {
    if (!this.interactiveTooltip) {
      return;
    }

    this.clearCloseTimeout();
    if (!tooltip.isOpen()) {
      tooltip.open();
    }
  }

  onBadgeLeave(tooltip: NgbTooltip): void {
    if (!this.interactiveTooltip) {
      return;
    }

    this.scheduleClose(tooltip);
  }

  onTooltipShown(badgeElement: HTMLElement, tooltip: NgbTooltip): void {
    if (!this.interactiveTooltip) {
      return;
    }

    this.detachTooltipListeners();

    const tooltipWindowId = badgeElement.getAttribute('aria-describedby');
    if (!tooltipWindowId) {
      return;
    }

    const tooltipWindow = this.document.getElementById(tooltipWindowId);
    if (!tooltipWindow) {
      return;
    }

    this.removeTooltipMouseEnterListener = this.renderer.listen(tooltipWindow, 'mouseenter', () => {
      this.clearCloseTimeout();
    });
    this.removeTooltipMouseLeaveListener = this.renderer.listen(tooltipWindow, 'mouseleave', () => {
      this.scheduleClose(tooltip);
    });
  }

  onTooltipHidden(): void {
    this.clearCloseTimeout();
    this.detachTooltipListeners();
  }

  ngOnDestroy(): void {
    this.clearCloseTimeout();
    this.detachTooltipListeners();
  }

  private scheduleClose(tooltip: NgbTooltip): void {
    this.clearCloseTimeout();

    this.closeTimeoutId = window.setTimeout(() => {
      this.detachTooltipListeners();
      if (tooltip.isOpen()) {
        tooltip.close();
      }
    }, this.closeDelay);
  }

  private clearCloseTimeout(): void {
    if (this.closeTimeoutId !== null) {
      window.clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }
  }

  private detachTooltipListeners(): void {
    this.removeTooltipMouseEnterListener?.();
    this.removeTooltipMouseLeaveListener?.();
    this.removeTooltipMouseEnterListener = null;
    this.removeTooltipMouseLeaveListener = null;
  }

}
